import { LayerType } from '../../shared/model/layers/layer-types'
import {
    createAnnotationShader,
    createMarkerShader,
    createSelectionsShader,
    createRGBShader,
} from '../../shared/utils/shaders'
import { environment } from '../../../environments/environment'
import {
    LayerConfig,
    LayerGroup,
    LayerGroupApiResponse,
    LayerIdApiResponse,
    NeuroglancerConfig,
    StaticLayerConfig,
    ViewerConfigApiRequest,
    ViewerConfigApiResponse,
    ViewerPosition,
    ViewerState,
} from './viewer.state'

function fixZoom(zoom: number | null | undefined): number | null | undefined {
    if (zoom === 0) {
        console.error('loaded crossSectionScale 0 from API (should not happen), using 1 instead')
        return 1
    }
    return zoom
}

export const mapApiResponseToViewerState = (config: ViewerConfigApiResponse): ViewerState => {
    let layerGroups: LayerGroup[]
    try {
        layerGroups = mapApiResponseToLayerGroups(config.state.groups)
    } catch (e: unknown) {
        console.warn('Failed to parse layer groups:', e)
        layerGroups = mapApiResponseToLayerGroups([
            {
                name: 'WorkaroundGroup',
                children: config.data.layers
                    .filter(layer => layer.id)
                    .map(layer => ({ id: layer.id! })),
                group_id: 'WorkaroundGroup',
            },
        ])
    }
    return {
        dimensions: config.data.dimensions,
        position: config.state.position,
        crossSectionScale: fixZoom(config.state.crossSectionScale),
        layout: config.state.layout ?? 'xy',
        expandedNodes: config.state.expandedNodes ?? [],
        layers: config.data.layers
            .map((staticLayer: StaticLayerConfig): LayerConfig | null => {
                const dynamicLayer = config.state.layers.find(l => l.id === staticLayer.id)
                return dynamicLayer ? { ...dynamicLayer, ...staticLayer } : staticLayer
            })
            .filter((layer: LayerConfig): layer is LayerConfig => layer !== null),
        layerGroups,
        originalLayers: config.data.layers,
        searchKey: '',
    }
}

const mapApiResponseToLayerGroups = (groups: LayerGroupApiResponse[]): LayerGroup[] => {
    const mapTreeRecursively = (group: LayerGroupApiResponse): LayerGroup => ({
        // Use name as id until backend provides a proper id
        id: group.group_id || group.name,
        name: group.name,
        layerIds: group.children.filter(isLayerIdApiResponse).map(layer => layer.id),
        children: group.children
            .filter(isLayerGroupApiResponse)
            .map(group => mapTreeRecursively(group)),
    })
    return groups.map(group => mapTreeRecursively(group))
}

const isLayerIdApiResponse = (
    value: LayerGroupApiResponse | LayerIdApiResponse
): value is LayerIdApiResponse => {
    return 'id' in value && !('children' in value)
}

const isLayerGroupApiResponse = (
    value: LayerGroupApiResponse | LayerIdApiResponse
): value is LayerGroupApiResponse => {
    return 'children' in value && !('id' in value)
}

export const mapViewerStateToNeuroglancerConfig = (config: ViewerState): NeuroglancerConfig => ({
    dimensions: config.dimensions,
    crossSectionScale: config.crossSectionScale || undefined,
    layout: config.layout ?? 'xy',
    position: isValidPosition(config.position) ? config.position : undefined,
    showAxisLines: false,
    layers: (config.layers ?? [])
        .filter((layer: LayerConfig) => layer.source?.startsWith('precomputed://'))
        .map((layer: LayerConfig) => ({
            ...layer,

            // archived: !layer.visible
            //
            // Failed attempt #1 to load layers on-demand: The "archived" flag
            // did not prevent neuroglancer from fetching the info JSON of each
            // layer. This fetching is a main cause why the loading time for ~500
            // layers is slow. Neuroglancer has code to automatically un-archive
            // layers when you set them to visible, so it seems a bit surprising
            // that this didin't help.
            //
            // Failed attempt #2: We can set a layer's source to "undefined", but
            // there are many problems to be solved (observables will work, etc.)
            // And setting an invalid source URL instead did not work at all.

            [layer.type === LayerType.Segmentation ? 'selectedAlpha' : 'opacity']:
                layer.opacity ?? 1,
            ...(layer.segmentationColor !== undefined &&
                layer.segmentationColor !== null && {
                    segmentDefaultColor: layer.segmentationColor,
                }),
            shader: isMarkerLayer(layer)
                ? createMarkerShader(
                      layer.shaderColor ?? null,
                      layer.brightness ?? null,
                      layer.contrast ?? null
                  )
                : isSelectionsLayer(layer)
                ? createSelectionsShader()
                : isAnnotationLayer(layer)
                ? createAnnotationShader(layer.opacity, layer.shaderColor)
                : isRawImageLayer(layer)
                ? createRGBShader()
                : layer.shader,
        })),
})

const isValidPosition = (pos: ViewerPosition | undefined | null): boolean => {
    return (
        pos !== null &&
        pos !== undefined &&
        pos.length === 3 &&
        pos[0] !== null &&
        pos[0] !== undefined &&
        pos[1] !== null &&
        pos[1] !== undefined &&
        pos[2] !== null &&
        pos[2] !== undefined
    )
}

const isMarkerLayer = (layer: LayerConfig): boolean => {
    return layer.type === LayerType.Image && !layer.name?.includes(environment.rawImageLayerToken)
}

const isSelectionsLayer = (layer: LayerConfig): boolean => {
    return layer.type === LayerType.Annotation && !!layer.isSelectable
}

const isAnnotationLayer = (layer: LayerConfig): boolean => {
    return layer.type === LayerType.Annotation
}

const isRawImageLayer = (layer: LayerConfig): boolean => {
    return (
        layer.type === LayerType.Image &&
        (layer.name ?? '').includes(environment.rawImageLayerToken)
    )
}

export const mapViewerStateToApiRequest = (config: ViewerState): ViewerConfigApiRequest => ({
    position: config.position,
    layout: config.layout,
    expandedNodes: config.expandedNodes,
    crossSectionScale: config.crossSectionScale,
    groups: config.layerGroups,
    layers:
        config.layers?.map(l => ({
            id: l.id,
            visible: l.visible,
            shaderColor: l.shaderColor,
            opacity: l.opacity,
            opacityUnselected: l.opacityUnselected,
            saturation: l.saturation,
            brightness: l.brightness,
            contrast: l.contrast,
            segmentationColor: l.segmentationColor ?? null,
        })) ?? [],
})

export const getLayerIdsForGroupId = (groupId: string, state: ViewerState): string[] => {
    const layerIds: string[] = []

    const getLayerIdsRecursively = (group: LayerGroup, isSubgroupOfNameMatch = false): void => {
        if (isSubgroupOfNameMatch || group.id === groupId) {
            layerIds.push(...group.layerIds)
            isSubgroupOfNameMatch = true
        }
        group.children.forEach(subGroup => {
            getLayerIdsRecursively(subGroup, isSubgroupOfNameMatch)
        })
    }

    state.layerGroups.forEach(group => {
        getLayerIdsRecursively(group)
    })

    return layerIds
}
